import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, CircularProgress, Paper, IconButton, Collapse, Typography, Tooltip,
} from '@mui/material';
import { getRequest } from '../UrlRequest';
import { useLocation } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { utils, writeFile } from 'xlsx';

const tableStyles = {
  tableContainer: {
    '& .MuiTable-root': {
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
    '& .MuiTableHead-root': {
      backgroundColor: '#f8f8f8',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      borderBottom: '2px solid #BDC3C7',
      fontWeight: 'bold',
      color: '#000',
      backgroundColor: '#f8f8f8',
      padding: '16px', // Increased padding to create more margin
    },
    '& .MuiTableCell-root': {
      border: '1px solid #dde2eb',
      borderTop: 'none',
      borderLeft: 'none',
      fontSize: 'calc(var(--base-font-size) * var(--scale-factor))',
    },
    '& .MuiTableRow-root:hover:not([data-highlighted="true"])': {
      backgroundColor: '#f4f4f4',
    },
    '& .MuiTableRow-root[data-highlighted="true"]': {
      backgroundColor: '#e3f2fd !important',
    },
    '& .MuiTableRow-root[data-highlighted="true"]:hover': {
      backgroundColor: '#bbdefb !important',
    },
    '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
      borderBottom: '1px solid #dde2eb',
    },
  }
};

const PatientGrid = ({ patientId, setSelectedDate, patient, onDataClick, cachedData, setCachedData }) => {
  const [rowData, setRowData] = useState([]);  // 명시적으로 빈 배열로 초기화
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [pageSize] = useState(30);
  const location = useLocation();
  const application = location.state?.application || 'hearton';
  const containerRef = useRef(null);
  const previousPatientId = useRef(null);
  const [initialLoad, setInitialLoad] = useState(false);
  const currentPatientRef = useRef(patientId);  // 현재 환자 ID를 추적하기 위한 ref

  // 캐시 크기 제한을 위한 함수
  const cleanupCache = useCallback((currentPatientId, newData) => {
    setCachedData(prev => {
      const updatedCache = { ...prev };
      
      // 현재 환자의 데이터가 너무 큰 경우 처리
      if (newData && newData.length > 100) {  // 100개 이상의 레코드인 경우
        console.log('🚨 Large dataset detected, limiting cache:', {
          patientId: currentPatientId,
          dataSize: newData.length
        });
        // 최근 50개 레코드만 유지
        updatedCache[currentPatientId] = {
          data: newData.slice(-50),
          page: 1,
          pageCount: Math.ceil(newData.length / pageSize),
          isTruncated: true  // 캐시가 잘린 것을 표시
        };
      }
      
      // 전체 캐시 크기 관리
      const cacheSize = Object.keys(updatedCache).length;
      if (cacheSize > 5) {  // 최대 5명의 환자 데이터만 캐시
        console.log('♻️ Cleaning up old cache entries');
        const sortedEntries = Object.entries(updatedCache)
          .sort(([, a], [, b]) => b.lastAccessed - a.lastAccessed)
          .slice(0, 5);
        return Object.fromEntries(sortedEntries);
      }
      
      return updatedCache;
    });
  }, [pageSize]);

  const fetchData = useCallback(async (currentPage, requestedPatientId) => {
    // 현재 요청하는 환자 ID와 현재 표시중인 환자 ID가 다르면 요청 중단
    if (requestedPatientId !== currentPatientRef.current) {
      console.log('🚫 Cancelled stale request:', {
        requested: requestedPatientId,
        current: currentPatientRef.current
      });
      return;
    }

    if (loading) {
      console.log('⏳ Previous request still loading, skipping');
      return;
    }

    if (!hasMore && currentPage > 1) {
      console.log('🛑 No more data to load');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      const data = await getRequest(
        application,
        `/doctor/health/${requestedPatientId}?page=${currentPage}&size=${pageSize}`,
        token
      );

      // 다시 한번 확인 - 비동기 요청 완료 후 환자가 바뀌었는지 체크
      if (requestedPatientId !== currentPatientRef.current) {
        console.log('🚫 Discarding stale response:', {
          requested: requestedPatientId,
          current: currentPatientRef.current
        });
        return;
      }

      const healthList = data.health_list || [];
      const pageCount = data.page_count || 1;
      
      console.log('📥 Received data:', {
        patientId: requestedPatientId,
        currentPage,
        pageCount,
        recordCount: healthList.length
      });

      const formattedData = healthList.map((record) => {
        const hasClinicalData = (record.blood_pressures && record.blood_pressures.length > 0) ||
                                record.weight != null ||
                                (record.heart_rates && record.heart_rates.length > 0);
        const hasHealthCondition = record.health_condition != null;
        
        // 디버깅 로그
        console.log(`🎯 ${record.on_date} - Clinical: ${hasClinicalData}, Condition: ${hasHealthCondition}, Highlight: ${hasHealthCondition && hasClinicalData}`, {
          blood_pressures: record.blood_pressures?.length || 0,
          weight: record.weight,
          heart_rates: record.heart_rates?.length || 0,
          health_condition: !!record.health_condition
        });

        return {
          on_date: record.on_date,
          systolic: record.blood_pressures?.[0]?.systolic || '미등록',
          diastolic: record.blood_pressures?.[0]?.diastolic || '미등록',
          weight: record.weight || '미등록',
          heart_rate: record.heart_rates?.[0]?.heart_rate || record.health_condition?.heart_rate || '미등록',
          health_condition: record.health_condition || {},
          blood_pressures: record.blood_pressures || [],
          heart_rates: record.heart_rates || [],
          expanded: false,
          highlight: hasHealthCondition && hasClinicalData,
        };
      });

      setRowData(prevData => {
        // 페이지가 1이면 새로운 데이터로 교체
        if (currentPage === 1) {
          return formattedData;
        }
        // 그 외의 경우 기존 데이터에 추가
        return [...prevData, ...formattedData];
      });

      setHasMore(currentPage < pageCount);
      setInitialLoad(false);

      // 캐시 업데이트
      setCachedData(prev => ({
        ...prev,
        [requestedPatientId]: {
          data: currentPage === 1 ? formattedData : [...(prev[requestedPatientId]?.data || []), ...formattedData],
          page: currentPage,
          pageCount: pageCount,
          lastAccessed: Date.now()
        }
      }));

    } catch (error) {
      console.error('Error fetching data:', error);
      if (requestedPatientId === currentPatientRef.current) {
        setError('데이터를 불러오는 중 오류가 발생했습니다.');
        setHasMore(false);
      }
    } finally {
      if (requestedPatientId === currentPatientRef.current) {
        setLoading(false);
      }
    }
  }, [application, pageSize, loading, hasMore]);

  const loadNextPage = useCallback(() => {
    if (!loading && hasMore) {
      console.log('📜 Loading next page:', {
        currentPatient: currentPatientRef.current,
        nextPage: page + 1,
        loading,
        hasMore
      });
      setPage(prevPage => prevPage + 1);
      fetchData(page + 1, currentPatientRef.current);
    } else {
      console.log('⚠️ Skip loading next page:', {
        loading,
        hasMore,
        currentPage: page
      });
    }
  }, [loading, hasMore, page, fetchData]);

  const handleScroll = useCallback((event) => {
    const target = event.target;
    if (!target) return;

    const { scrollTop, scrollHeight, clientHeight } = target;
    const threshold = 100; // 스크롤이 바닥에서 100px 이내일 때
    
    if (scrollHeight - scrollTop <= clientHeight + threshold) {
      console.log('📊 Scroll position:', {
        scrollTop,
        scrollHeight,
        clientHeight,
        remaining: scrollHeight - scrollTop - clientHeight
      });
      loadNextPage();
    }
  }, [loadNextPage]);

  // 스크롤 이벤트 리스너 설정
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const debouncedScroll = debounce(handleScroll, 150);
    
    console.log('🎯 Setting up scroll listener');
    container.addEventListener('scroll', debouncedScroll);
    
    return () => {
      console.log('🧹 Cleaning up scroll listener');
      container.removeEventListener('scroll', debouncedScroll);
      debouncedScroll.cancel();
    };
  }, [handleScroll]);

  // debounce 유틸리티 함수
  function debounce(func, wait) {
    let timeout;
    const debouncedFn = function(...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
    debouncedFn.cancel = () => clearTimeout(timeout);
    return debouncedFn;
  }

  useEffect(() => {
    if (previousPatientId.current !== patientId) {
      console.log('👤 Patient changed:', {
        from: previousPatientId.current,
        to: patientId
      });

      // 현재 환자 ID 업데이트
      currentPatientRef.current = patientId;
      
      // 상태 완전 초기화
      setRowData([]);
      setPage(1);
      setHasMore(true);
      setInitialLoad(false); // initialLoad를 false로 설정하여 스크롤 로딩이 즉시 작동하도록 함
      setError(null);
      setLoading(false);
      
      previousPatientId.current = patientId;

      // 캐시된 데이터 확인 및 로드
      const cachedPatientData = cachedData[patientId];
      if (cachedPatientData && !cachedPatientData.isTruncated) {
        console.log('📦 Using cached data:', {
          patientId,
          dataSize: cachedPatientData.data.length,
          page: cachedPatientData.page,
          pageCount: cachedPatientData.pageCount
        });
        
        setRowData(cachedPatientData.data);
        setPage(cachedPatientData.page || 1);
        setHasMore(cachedPatientData.page < cachedPatientData.pageCount);
        
        // 캐시 접근 시간 업데이트
        setCachedData(prev => ({
          ...prev,
          [patientId]: {
            ...prev[patientId],
            lastAccessed: Date.now()
          }
        }));
      } else {
        console.log('🔄 Fetching fresh data for new patient:', patientId);
        fetchData(1, patientId);
      }
    }
  }, [patientId, fetchData, cachedData, setCachedData]);

  const toggleRowExpansion = (index) => {
    setRowData((prevData) =>
      prevData.map((row, i) => (i === index ? { ...row, expanded: !row.expanded } : row))
    );
  };

  const handleRowClick = (date) => {
    setSelectedDate(date);
    if (onDataClick) {
      onDataClick(date);
    }
  };

  const Row = ({ row, index }) => {
    const [open, setOpen] = useState(false);
    
    return (
      <>
        <TableRow 
          hover 
          onClick={() => handleRowClick(row.on_date)}
          data-highlighted={row.highlight}
          style={{ 
            cursor: 'pointer'
          }}
        >
          <TableCell>
            <IconButton onClick={(e) => {
              e.stopPropagation();
              toggleRowExpansion(index);
            }}>
              {row.expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell>{row.on_date || ''}</TableCell>
          <TableCell>{row.systolic || '미등록'}</TableCell>
          <TableCell>{row.diastolic || '미등록'}</TableCell>
          <TableCell>{row.weight || '미등록'}</TableCell>
          <TableCell>{row.heart_rate || '미등록'}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={row.expanded} timeout="auto" unmountOnExit>
              <Table size="small" aria-label="시간별 데이터">
                <TableHead>
                  <TableRow>
                    <TableCell>시간</TableCell>
                    <TableCell>수축기 혈압</TableCell>
                    <TableCell>이완기 혈압</TableCell>
                    <TableCell>심박수</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const allTimes = Array.from(new Set([
                      ...(row.blood_pressures || []).map(bp => bp.on_time),
                      ...(row.heart_rates || []).map(hr => hr.on_time)
                    ])).sort((a, b) => b.localeCompare(a));

                    return allTimes.map((time, i) => {
                      const bp = (row.blood_pressures || []).find(bp => bp.on_time === time) || {};
                      const hr = (row.heart_rates || []).find(hr => hr.on_time === time) || {};

                      return (
                        <TableRow key={`${row.on_date}-${time}-${i}`}>
                          <TableCell>{time || ''}</TableCell>
                          <TableCell>{bp.systolic || '미등록'}</TableCell>
                          <TableCell>{bp.diastolic || '미등록'}</TableCell>
                          <TableCell>{hr.heart_rate || '미등록'}</TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  };

  // 데이터 변환 함수 개선
  const getExportData = () => {
    if (!Array.isArray(rowData)) return [];
    
    return rowData.reduce((acc, record) => {
      if (!record) return acc;
      
      const baseData = {
        날짜: record.on_date || '',
        수축기혈압: '',
        이완기혈압: '',
        시간: '',
        체중: record.weight || '미등록',
        심박수: record.heart_rate || '미등록',
      };
    
      if (!record.blood_pressures?.length && !record.heart_rates?.length) {
        return [...acc, baseData];
      }
    
      const times = Array.from(new Set([
        ...(record.blood_pressures || []).map((bp) => bp.on_time),
        ...(record.heart_rates || []).map((hr) => hr.on_time),
      ])).sort((a, b) => b.localeCompare(a));
    
      const timeData = times.map((time) => {
        const bpData = (record.blood_pressures || []).find((bp) => bp.on_time === time) || {};
        const hrData = (record.heart_rates || []).find((hr) => hr.on_time === time) || {};
    
        return {
          ...baseData,
          수축기혈압: bpData.systolic || '미등록',
          이완기혈압: bpData.diastolic || '미등록',
          심박수: hrData.heart_rate || '미등록',
          시간: time || '미등록'
        };
      });
      
      return [...acc, ...timeData];
    }, []);
  };

  const handleExcelDownload = () => {
    const ws = utils.json_to_sheet(getExportData());
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "환자데이터");
    writeFile(wb, `${patient.name}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div style={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
        <CSVLink 
          data={getExportData()} 
          filename={`${patient?.name || 'patient'}_${new Date().toISOString().split('T')[0]}.csv`}
          style={{ textDecoration: 'none', flex: 1 }}
        >
          <Tooltip title="테이블 차트의 데이터로만 내보냅니다" placement="top">
            <Button
              variant="contained" 
              color="primary" 
              style={{
                backgroundColor: '#2196f3',
                textTransform: 'none',
                width: '100%'
              }}
            >
              CSV 내보내기
            </Button>
          </Tooltip>
        </CSVLink>
        <Tooltip title="테이블 차트의 데이터로만 내보냅니다" placement="top">
          <Button
            variant="contained"
            color="primary"
            onClick={handleExcelDownload}
            style={{
              backgroundColor: '#2196f3',
              textTransform: 'none',
              flex: 1
            }}
          >
            Excel 내보내기
          </Button>
        </Tooltip>
      </div>
      <TableContainer
      component={Paper}
      style={{
        height: 'calc(100% - 45px)', // 버튼 높이(36px)와 마진(10px)을 고려한 계산
        overflowY: 'auto',
        flex: 1,
        position: 'relative',
        border: '1px solid #dde2eb',
        borderRadius: '4px',
        boxShadow: 'none',
      }}
      ref={containerRef}
      sx={tableStyles.tableContainer}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow sx={{ '& th': {padding: '0px'}}}>
              <TableCell />
              <TableCell>날짜</TableCell>
              <TableCell>수축기 혈압</TableCell>
              <TableCell>이완기 혈압</TableCell>
              <TableCell>체중</TableCell>
              <TableCell>심박수</TableCell>
             </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(rowData) && rowData.map((row, index) => (
              <Row key={`${row.on_date}-${index}`} row={row} index={index} />
            ))}
          </TableBody>
        </Table>
        {loading && (
          <div style={{ 
            textAlign: 'center', 
            padding: '10px',
            position: 'sticky',  // loading 인디케이터를 고정
            bottom: 0,
            backgroundColor: 'white'
          }}>
            <CircularProgress />
          </div>
        )}
</TableContainer>
    </div>
  );
};

export default PatientGrid;
